import dayjs from 'dayjs';
import type { Search, Source } from '../../../services/abrangencia';
import type {
  AbrangenciaStatusRow,
  StatusDashboardOverviewResponse,
  StatusDashboardTopErrorRow,
} from '../../../services/statusDashboardService';
import {
  DEFAULT_KEY,
  ALL_KEY,
  STATUS_META,
  TRIBUNAL_GROUP_ORDER,
  type TribunalGroup,
} from './constants';

export type KeyOption = { value: string; label: string };

export const getApiErrorMessage = (e: unknown, fallback: string): string => {
  if (e instanceof Error && e.message) return e.message;
  const axiosData = (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
  return axiosData?.message || axiosData?.error || fallback;
};

/** Formata timestamp ISO do cache para dd/mm hh:mm:ss */
export const formatCacheStoredAt = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  return dayjs(iso).format('DD/MM HH:mm:ss');
};

export const buildCacheTagLabel = (fromCache: boolean, cachedAt?: string | null): string => {
  if (!fromCache) return 'Ao vivo';
  const formatted = formatCacheStoredAt(cachedAt);
  return formatted ? `Cache ${formatted}` : 'Cache';
};

export const getCacheTagTooltip = (fromCache: boolean, cachedAt?: string | null): string => {
  if (!fromCache) return 'Dados carregados diretamente do banco';
  const formatted = formatCacheStoredAt(cachedAt);
  return formatted ? `Dados obtidos em ${formatted}` : 'Dados servidos do cache Redis';
};

/** Formata segundos em texto legível (ex: 2m 30s) */
export const formatDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;

  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins < 60) return `${mins}m ${secs}s`;

  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hours}h ${remMins}m`;
};

export const buildRowKey = (row: AbrangenciaStatusRow, index: number): string =>
  [row.search, row.platform, row.query, row.key, index].join('::');

/** Formata intervalo PostgreSQL (tempo_em_erro) em dias */
export const formatIntervalDays = (value: unknown): string => {
  if (value == null) return '-';

  let totalDays = 0;

  if (typeof value === 'object') {
    const interval = value as {
      days?: number;
      hours?: number;
      minutes?: number;
      seconds?: number;
    };
    totalDays =
      (interval.days ?? 0)
      + (interval.hours ?? 0) / 24
      + (interval.minutes ?? 0) / 1440
      + (interval.seconds ?? 0) / 86400;
  } else {
    const str = String(value);
    const daysMatch = str.match(/(-?\d+)\s+days?/i);
    const timeMatch = str.match(/(\d+):(\d+):(\d+)/);

    totalDays = daysMatch ? parseInt(daysMatch[1], 10) : 0;
    if (timeMatch) {
      totalDays +=
        parseInt(timeMatch[1], 10) / 24
        + parseInt(timeMatch[2], 10) / 1440
        + parseInt(timeMatch[3], 10) / 86400;
    }
    if (!daysMatch && !timeMatch) return '-';
  }

  if (totalDays < 1) return '< 1 dia';
  const rounded = Math.round(totalDays * 10) / 10;
  if (rounded === 1) return '1 dia';
  return Number.isInteger(rounded) ? `${rounded} dias` : `${rounded.toFixed(1)} dias`;
};

export const buildConsecutiveSessionKey = (page: number, key: string): string =>
  `consecutive:${key}:p${page}`;

export const buildErrorsSessionKey = (page: number, comparison: string, key: string): string =>
  `errors:${comparison}:${key}:p${page}`;

export const buildSlowSessionKey = (page: number, selectedPeriod: string, key: string): string =>
  `slow:${selectedPeriod}:${key}:p${page}`;

export const buildApiConsultaSessionKey = (days: number): string => `api-consulta:days=${days}`;

export const buildApiConsultaCnjSessionKey = (days: number): string => `api-consulta-cnj:days=${days}`;

export const buildKeyOptions = (keys?: string[]): KeyOption[] => {
  const normalizedKeys = Array.from(
    new Set([DEFAULT_KEY, ...(keys || []).map((key) => String(key).trim()).filter(Boolean)]),
  );

  return [
    { value: ALL_KEY, label: 'todos' },
    ...normalizedKeys.map((key) => ({ value: key, label: key })),
  ];
};

export const formatNumber = (value: number): string => new Intl.NumberFormat('pt-BR').format(value);

/** Resumo de registros da tabela paginada: total obtido com os filtros e quantidade exibida na página. */
export const formatTableRecordSummary = (total: number, displayed: number): string => {
  const obtidosLabel = total === 1 ? 'registro obtido' : 'registros obtidos';
  const exibidosLabel = displayed === 1 ? 'registro exibido' : 'registros exibidos';
  return `${formatNumber(total)} ${obtidosLabel} · ${formatNumber(displayed)} ${exibidosLabel}`;
};

export const formatPercent = (value: number): string => `${value.toFixed(value % 1 === 0 ? 0 : 1)}%`;

/** Converte ângulo em graus (0 = topo) para coordenadas cartesianas. */
export const polarToCartesian = (cx: number, cy: number, radius: number, angleDegrees: number) => {
  const radians = ((angleDegrees - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
};

/**
 * Gera o path SVG de um segmento de donut com bordas radiais retas.
 * Evita artefatos visuais do stroke-dasharray em fatias pequenas.
 */
export const createDonutSegmentPath = (
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  startAngle: number,
  endAngle: number,
): string => {
  const sweepAngle = endAngle - startAngle;
  if (sweepAngle <= 0) return '';

  const safeEndAngle = sweepAngle >= 359.99 ? startAngle + 359.99 : endAngle;
  const largeArcFlag = safeEndAngle - startAngle > 180 ? 1 : 0;

  const startOuter = polarToCartesian(cx, cy, outerRadius, startAngle);
  const endOuter = polarToCartesian(cx, cy, outerRadius, safeEndAngle);
  const startInner = polarToCartesian(cx, cy, innerRadius, safeEndAngle);
  const endInner = polarToCartesian(cx, cy, innerRadius, startAngle);

  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${endOuter.x} ${endOuter.y}`,
    `L ${startInner.x} ${startInner.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${endInner.x} ${endInner.y}`,
    'Z',
  ].join(' ');
};

const normalizeText = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

export const getTribunalGroup = (value: string): TribunalGroup | 'outros' => {
  const normalized = normalizeText(value);
  if (normalized.startsWith('trf') || normalized.includes('federal')) return 'federal';
  if (normalized.startsWith('trt') || normalized.includes('trabalhista')) return 'trabalhista';
  if (normalized.startsWith('tj') || normalized.includes('estadual')) return 'estadual';
  if (
    normalized.startsWith('stj') ||
    normalized.startsWith('stf') ||
    normalized.startsWith('tst') ||
    normalized.startsWith('stm') ||
    normalized.startsWith('tse') ||
    normalized.includes('superior')
  ) {
    return 'superior';
  }
  return 'outros';
};

export const collectAvailableSearches = (sources: Source[] | null | undefined): Search[] => {
  if (!Array.isArray(sources)) return [];

  return sources.flatMap((source) => source.platforms.flatMap((platform) => platform.searches));
};

export const getSummaryChartMetrics = (summary?: StatusDashboardOverviewResponse['statusSummary']) => {
  if (!summary) return [];

  return STATUS_META.map((item) => ({
    ...item,
    total: summary[item.key].total,
    percentage: summary[item.key].percentage,
  }));
};

export const aggregateTribunalErrors = (
  items: StatusDashboardTopErrorRow[] | undefined,
  availableSearches: Search[],
): StatusDashboardTopErrorRow[] => {
  const rows = Array.isArray(items) ? items : [];
  const grouped = new Map<TribunalGroup, number>();
  const searchGroupMap = new Map<string, TribunalGroup>();

  TRIBUNAL_GROUP_ORDER.forEach((group) => grouped.set(group, 0));
  availableSearches.forEach((search) => {
    const group = getTribunalGroup(search.search);
    if (group !== 'outros') {
      searchGroupMap.set(normalizeText(search.search), group);
    }
  });

  rows.forEach((row) => {
    const groupLabel = searchGroupMap.get(normalizeText(row.label)) || getTribunalGroup(row.label);

    if (groupLabel === 'outros') return;
    grouped.set(groupLabel, (grouped.get(groupLabel) || 0) + Number(row.total || 0));
  });

  const total = Array.from(grouped.values()).reduce((acc, value) => acc + value, 0);
  return TRIBUNAL_GROUP_ORDER.map((group) => {
    const groupTotal = grouped.get(group) || 0;
    return {
      label: group,
      total: groupTotal,
      percentage: total > 0 ? Math.round((groupTotal / total) * 1000) / 10 : 0,
    };
  });
};

export const isPanelOpen = (keys: string | string[] | undefined, panelKey: string): boolean =>
  Array.isArray(keys) ? keys.includes(panelKey) : keys === panelKey;
